/**
 * Loyalty tier computation — shared between customer portal and admin views.
 *
 * Tiers are determined by lifetimeEarned points against thresholds.
 * Default tiers match LoyaltySettingsPage defaults.
 */

export interface LoyaltyTier {
  id: string;
  name: string;
  minPoints: number;
  pointsMultiplier: number;
  color: string;
}

export const DEFAULT_TIERS: LoyaltyTier[] = [
  { id: 'bronze', name: 'Bronze', minPoints: 0, pointsMultiplier: 1.0, color: '#cd7f32' },
  { id: 'silver', name: 'Silver', minPoints: 2500, pointsMultiplier: 1.25, color: '#c0c0c0' },
  { id: 'gold', name: 'Gold', minPoints: 10000, pointsMultiplier: 1.5, color: '#c9a84c' },
  { id: 'platinum', name: 'Platinum', minPoints: 25000, pointsMultiplier: 2.0, color: '#e5e4e2' },
];

export interface TierInfo {
  current: LoyaltyTier;
  next: LoyaltyTier | null;
  progress: number; // 0-100 percentage to next tier
  pointsToNext: number;
}

/**
 * Compute the current tier and progress to next tier.
 */
export function computeTier(lifetimeEarned: number, tiers?: LoyaltyTier[]): TierInfo {
  const sortedTiers = [...(tiers || DEFAULT_TIERS)].sort((a, b) => b.minPoints - a.minPoints);

  let current = sortedTiers[sortedTiers.length - 1]; // lowest tier as default
  let currentIndex = sortedTiers.length - 1;

  for (let i = 0; i < sortedTiers.length; i++) {
    if (lifetimeEarned >= sortedTiers[i].minPoints) {
      current = sortedTiers[i];
      currentIndex = i;
      break;
    }
  }

  // Next tier is one level up (lower index in descending-sorted array)
  const next = currentIndex > 0 ? sortedTiers[currentIndex - 1] : null;

  let progress = 100;
  let pointsToNext = 0;

  if (next) {
    const rangeStart = current.minPoints;
    const rangeEnd = next.minPoints;
    const earned = lifetimeEarned - rangeStart;
    const range = rangeEnd - rangeStart;
    progress = Math.min(100, Math.floor((earned / range) * 100));
    pointsToNext = Math.max(0, rangeEnd - lifetimeEarned);
  }

  return { current, next, progress, pointsToNext };
}
